import { createContext, type JSX, type ReactNode, useContext, useState } from 'react';
import { type Location, useLocation, useNavigationType } from 'react-router';

const MemberBackHistoryContext = createContext<boolean | undefined>(undefined);

function isMemberEntry(path: string): boolean {
  return (
    !/^\/(?:login|auth|admin|cms)(?:\/|$)/u.test(path) &&
    path !== '/ei-jasenyytta' &&
    path !== '/tunnistus-epaonnistui'
  );
}

interface HistoryState {
  readonly entries: readonly Pick<Location, 'key' | 'pathname'>[];
  readonly index: number;
}

/** Only entries observed in this document can establish an eligible predecessor. */
export function MemberBackHistory({ children }: { readonly children: ReactNode }): JSX.Element {
  const location = useLocation();
  const action = useNavigationType();
  const entry = { key: location.key, pathname: location.pathname };
  const [history, setHistory] = useState<HistoryState>(() => ({ entries: [entry], index: 0 }));
  let current = history;
  if (history.entries[history.index]?.key !== location.key) {
    if (action === 'PUSH') {
      current = {
        entries: [...history.entries.slice(0, history.index + 1), entry],
        index: history.index + 1,
      };
    } else if (action === 'REPLACE') {
      current = {
        entries: history.entries.map((entry, index) =>
          index === history.index ? { key: location.key, pathname: location.pathname } : entry,
        ),
        index: history.index,
      };
    } else {
      const index = history.entries.findIndex((entry) => entry.key === location.key);
      current = index === -1 ? { entries: [entry], index: 0 } : { ...history, index };
    }
    setHistory(current);
  }
  const previous = current.entries[current.index - 1];
  const canGoBack =
    previous !== undefined && isMemberEntry(previous.pathname) && isMemberEntry(location.pathname);
  return (
    <MemberBackHistoryContext.Provider value={canGoBack}>
      {children}
    </MemberBackHistoryContext.Provider>
  );
}

export function useMemberBackHistory(): boolean | undefined {
  return useContext(MemberBackHistoryContext);
}
